<script setup lang="ts">
/**
 * NotificationPanel — Dropdown panel showing pending product requests.
 *
 * Opened by clicking the bell icon in AppHeader.
 * Each item navigates to the detail modal on click.
 */
import { Bell, Loader2, AlertCircle } from 'lucide-vue-next';
import { useNotificationsStore } from '@/stores/notifications.store';
import { formatDistanceToNow } from 'date-fns';

const store = useNotificationsStore();

function handleItemClick(requestId: string) {
  store.closePanel();
  store.loadRequestDetail(requestId);
}

function formatTime(dateStr: string): string {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return dateStr;
  }
}
</script>

<template>
  <!-- Click-outside backdrop -->
  <div
    v-if="store.showPanel"
    class="fixed inset-0 z-40"
    @click="store.closePanel()"
  />
  <div
    v-if="store.showPanel"
    class="absolute right-0 top-full mt-2 w-96 max-h-[28rem] overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5 dark:bg-gray-800 dark:ring-gray-700 z-50"
  >
    <!-- Header -->
    <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
        Product Requests
      </h3>
      <span
        v-if="store.pendingCount > 0"
        class="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-300"
      >
        {{ store.pendingCount }} pending
      </span>
    </div>

    <!-- Loading -->
    <div
      v-if="store.isLoadingList"
      class="flex items-center justify-center py-8"
    >
      <Loader2 class="h-6 w-6 animate-spin text-primary-500" />
      <span class="ml-2 text-sm text-gray-500">Loading requests...</span>
    </div>

    <!-- Error -->
    <div
      v-else-if="store.error && store.pendingRequests.length === 0"
      class="flex items-center gap-2 px-4 py-6 text-sm text-red-600 dark:text-red-400"
    >
      <AlertCircle class="h-5 w-5 shrink-0" />
      <span>{{ store.error }}</span>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!store.pendingRequests || store.pendingRequests.length === 0"
      class="flex flex-col items-center justify-center py-8 text-gray-400"
    >
      <Bell class="h-8 w-8 mb-2" />
      <p class="text-sm">No pending requests</p>
    </div>

    <!-- List -->
    <div
      v-else
      class="max-h-80 overflow-y-auto"
    >
      <button
        v-for="item in store.pendingRequests"
        :key="item.request_id"
        type="button"
        class="w-full px-4 py-3 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
        @click="handleItemClick(item.request_id)"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ item.suggested_product_name }}
            </p>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {{ item.request_reference }} · {{ item.hospital_name }}
            </p>
            <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
              {{ formatTime(item.requested_at) }}
            </p>
          </div>
          <span class="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-300 shrink-0">
            Pending
          </span>
        </div>
      </button>
    </div>
  </div>
</template>
